<script setup lang="ts">
import type { Extensions, JSONContent } from "@tiptap/core";
import { Document } from "@tiptap/extension-document";
import { Heading } from "@tiptap/extension-heading";
import { Text } from "@tiptap/extension-text";
import { Bold } from "@tiptap/extension-bold";
import { Italic } from "@tiptap/extension-italic";
import { Strike } from "@tiptap/extension-strike";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Twemoji } from "~/tiptap/twemoji";
import { Paragraph } from "@tiptap/extension-paragraph";
import { CodeBlock } from "@tiptap/extension-code-block";
import { Blockquote } from "@tiptap/extension-blockquote";
import { ListItem } from "@tiptap/extension-list-item";
import { OrderedList } from "@tiptap/extension-ordered-list";
import { BulletList } from "@tiptap/extension-bullet-list";
import { HorizontalRule } from '@tiptap/extension-horizontal-rule'
import { Underline } from "@tiptap/extension-underline";
import { Code } from "@tiptap/extension-code";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Link } from "@tiptap/extension-link";
import { UserMention } from "~/tiptap/mention";
import { HighLightSection } from "~/tiptap/highlight-section";
import { Figcaption, Figure } from "~/tiptap/figure";
import { Gallery } from "~/tiptap/gallery";
import { CharonFile } from "~/tiptap/charon-file";
import { Youtube } from "~/tiptap/youtube";
import { Keyboard } from "~/tiptap/keyboard";
import { Placeholder } from "@tiptap/extension-placeholder";
import { History } from "@tiptap/extension-history";
import { Dropcursor } from "@tiptap/extension-dropcursor";
import { Gapcursor } from "@tiptap/extension-gapcursor";
import { Typography } from "@tiptap/extension-typography";
import { HocuspocusProvider } from "@hocuspocus/provider";
import type EditorMarkMenu from "~/components/editor/EditorMarkMenu.vue";

const { t } = useI18n()

const props = defineProps<{
  provider: HocuspocusProvider | null
  field?: string
  content: JSONContent | null
  editable?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:content', value: JSONContent | null): void
}>()

const marks = ref<typeof EditorMarkMenu| null>(null)

const extensions = computed(() => [
  // nodes
  Document,
  Heading.configure({
    levels: [ 2, 3, 4 ]
  }),
  Text,
  Paragraph,
  CodeBlock,
  Blockquote,
  ListItem,
  OrderedList,
  BulletList,
  HorizontalRule,
  Twemoji,
  HighLightSection,
  Figcaption, Figure,
  Gallery,
  CharonFile,
  HardBreak,
  UserMention,
  Youtube.configure({
    nocookie: true,
  }),
  // marks
  Bold,
  Italic,
  Strike,
  Underline,
  Code,
  Subscript,
  Superscript,
  Link.configure({
    openOnClick: props.editable,
  }),
  // utils
  props.provider ? null : History,
  Placeholder.configure({
    showOnlyCurrent: false,
    placeholder: t('jc.component.newsEditorBody.placeholder'),
  }),
  Dropcursor,
  Gapcursor,
  Typography,
  Keyboard.configure({
    handler: (type) => {
      switch (type) {
        case "link":
          marks.value?.toggleLink()
          return true
      }
      return false
    }
  }),
].filter(e => e !== null) as Extensions)
</script>

<template>
  <CollaborationEditor
    :provider="props.provider"
    :field="props.field"
    :content="props.content"
    @update:content="val => emit('update:content', val)"
    :extensions="extensions"
    :editable="props.editable"
    #default="{ editor }"
  >
    <EditorMarkMenu ref="marks" :editor="editor" />
    <EditorGalleryMenu :editor="editor" />
    <EditorBlockMenu :editor="editor" :headings="[2, 3, 4]" />
  </CollaborationEditor>
</template>
